"use client";

import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { FormEvent, useState } from "react";

export function LoginForm() {
  const router = useRouter();
  const [showResetForm, setShowResetForm] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");
  const [isResetPending, setIsResetPending] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError("");
    setIsPending(true);

    const result = await signIn("credentials", {
      email,
      password,
      redirect: false,
      callbackUrl: "/dashboard",
    });

    setIsPending(false);

    if (result?.ok && result.url) {
      router.push(result.url);
      router.refresh();
      return;
    }

    setError("E-Mail oder Passwort ist nicht korrekt.");
  };

  const handleResetSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setResetError("");
    setResetSuccess("");
    setIsResetPending(true);

    try {
      const response = await fetch("/api/password-reset/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });

      const payload = (await response.json().catch(() => null)) as { error?: string } | null;
      if (!response.ok) {
        setResetError(payload?.error ?? "Anfrage fehlgeschlagen.");
        return;
      }

      setResetSuccess(
        "Wenn ein Konto mit dieser E-Mail existiert, wurde ein Link zum Zurücksetzen versendet.",
      );
    } catch {
      setResetError("Anfrage fehlgeschlagen.");
    } finally {
      setIsResetPending(false);
    }
  };

  if (showResetForm) {
    return (
      <form className="stack-sm" onSubmit={handleResetSubmit}>
        <h2 className="auth-subtitle">Passwort vergessen</h2>
        <p className="muted">Gib deine E-Mail ein, dann bekommst du einen Link zum Zurücksetzen.</p>

        <label className="field">
          <span>E-Mail</span>
          <input
            autoComplete="email"
            name="resetEmail"
            onChange={(event) => setResetEmail(event.target.value)}
            required
            type="email"
            value={resetEmail}
          />
        </label>

        {resetError ? <p className="error-text">{resetError}</p> : null}
        {resetSuccess ? <p className="feedback-success">{resetSuccess}</p> : null}

        <button className="btn btn-primary" disabled={isResetPending} type="submit">
          {isResetPending ? "Sende Link..." : "Reset-Link senden"}
        </button>

        <button
          className="btn btn-outline"
          onClick={() => {
            setShowResetForm(false);
            setResetError("");
            setResetSuccess("");
            setResetEmail(email);
          }}
          type="button"
        >
          Zurück zum Login
        </button>
      </form>
    );
  }

  return (
    <form className="stack-sm" onSubmit={handleSubmit}>
      <label className="field">
        <span>E-Mail</span>
        <input
          autoComplete="email"
          name="email"
          onChange={(event) => setEmail(event.target.value)}
          required
          type="email"
          value={email}
        />
      </label>

      <label className="field">
        <span>Passwort</span>
        <input
          autoComplete="current-password"
          name="password"
          onChange={(event) => setPassword(event.target.value)}
          required
          type="password"
          value={password}
        />
      </label>

      {error ? <p className="error-text">{error}</p> : null}

      <button className="btn btn-primary" disabled={isPending} type="submit">
        {isPending ? "Anmeldung läuft..." : "Einloggen"}
      </button>

      <button
        className="btn btn-quiet btn-fit"
        onClick={() => {
          setResetEmail(email);
          setShowResetForm(true);
        }}
        type="button"
      >
        Passwort vergessen?
      </button>
    </form>
  );
}
